"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/utils";
import type { OptionRendererProps } from "../types";

export interface ButtonOptionProps extends OptionRendererProps {
	/** Optional prefix for accessible label (e.g., "Size" → "Size M") */
	labelPrefix?: string;
	/** Minimum width of the button */
	minWidth?: string;
}

/**
 * Renders a variant option as a rectangular button.
 *
 * Visual states:
 * - Normal: Full styling, clickable
 * - Selected: Inverted colors (dark bg, light text)
 * - Incompatible: Dimmed border/text, still clickable - will clear other selections
 * - Out of stock: Strikethrough, disabled
 * - On sale: Small discount badge
 */
export function ButtonOption({
	option,
	isSelected,
	onSelect,
	isPending,
	labelPrefix,
	minWidth = "2.5rem",
}: ButtonOptionProps) {
	const t = useTranslations("pdpUx");
	const isOutOfStock = !option.available;
	const isIncompatible = option.existsWithCurrentSelection === false && !isSelected;
	const hasDiscount = option.discountPercent && !isOutOfStock;

	// Build accessible label with context
	const accessibleParts = [
		labelPrefix ? `${labelPrefix} ${option.name}` : option.name,
		isOutOfStock && "out of stock",
		option.unitPrice &&
			`${formatMoney(option.unitPrice.amount, option.unitPrice.currency)} per ${option.unitLabel ?? "piece"}`,
		hasDiscount && `${option.discountPercent}% off`,
	].filter(Boolean);

	return (
		<div
			className={cn(
				"relative transition-opacity duration-150",
				isPending && "pointer-events-none opacity-60",
			)}
			style={{ transitionDelay: isPending ? "100ms" : "0ms" }}
		>
			<button
				type="button"
				onClick={() => onSelect(option.id)}
				disabled={isOutOfStock || isPending}
				aria-disabled={isOutOfStock || isPending}
				style={{ minWidth }}
				className={cn(
					"inline-flex h-10 items-center justify-center rounded-full px-4 font-mono text-[0.68rem] font-semibold uppercase tracking-[0.18em] shadow-md transition-colors",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
					isSelected
						? "bg-primary-hero text-primary-hero-foreground hover:bg-primary"
						: isIncompatible
							? "hover:border-primary-hero/30 border border-border bg-background text-muted-foreground"
							: "hover:bg-primary-hero/5 border border-border bg-background text-primary-hero hover:border-primary-hero",
					isOutOfStock && "cursor-not-allowed text-muted-foreground line-through opacity-60",
				)}
				title={
					isOutOfStock
						? `${option.name} - Out of stock`
						: isIncompatible
							? `${option.name} - Will change other selections`
							: option.discountPercent
								? `${option.name} - ${option.discountPercent}% off`
								: undefined
				}
				aria-label={accessibleParts.join(", ")}
				aria-pressed={isSelected}
			>
				<span className="flex flex-col items-start leading-tight">
					<span>{option.name}</span>
					{option.unitPrice && (
						<span className={cn("text-xs", isSelected ? "text-background/80" : "text-muted-foreground")}>
							{formatMoney(option.unitPrice.amount, option.unitPrice.currency)} /{" "}
							{option.unitLabel ?? t("variantPiece")}
						</span>
					)}
				</span>
			</button>
			{hasDiscount && (
				<span
					className="pointer-events-none absolute -bottom-2 -right-1 rounded-full border border-destructive bg-background px-1.5 py-0.5 text-[10px] font-semibold text-destructive"
					aria-hidden="true"
				>
					-{option.discountPercent}%
				</span>
			)}
			{option.savingsAmount != null && option.savingsAmount > 0 && option.unitPrice && (
				<span className="mt-0.5 text-xs font-medium text-green-600">
					{t("variantSave", { amount: formatMoney(option.savingsAmount, option.unitPrice.currency) })}
				</span>
			)}
		</div>
	);
}

// Backwards-compatible aliases
export function SizeButtonOption(props: OptionRendererProps) {
	return <ButtonOption {...props} labelPrefix="Size" />;
}

export function TextOption(props: OptionRendererProps) {
	return <ButtonOption {...props} minWidth="3rem" />;
}
